import { useState, useEffect, useCallback, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { articlesApi, type Article, type Revision } from '../utils/api'

type Props = {
  article?: Article
  onSaved: (id: string) => void
  onCancel: () => void
}

type Toast = { type: 'success' | 'error'; message: string } | null

export default function ArticleEditor({ article, onSaved, onCancel }: Props) {
  // ── Form state ──────────────────────────────────────────────────────────────
  const [slug, setSlug]           = useState(article?.slug ?? '')
  const [title, setTitle]         = useState(article?.title ?? '')
  const [category, setCategory]   = useState(article?.category ?? '')
  const [tags, setTags]           = useState(article?.tags ?? '')
  const [metaTitle, setMetaTitle] = useState(article?.meta_title ?? '')
  const [metaDesc, setMetaDesc]   = useState(article?.meta_description ?? '')
  const [isPublished, setIsPublished] = useState(article?.is_published === 1)
  const [markdown, setMarkdown]   = useState('')

  // ── UI state ────────────────────────────────────────────────────────────────
  const [saving, setSaving]       = useState(false)
  const [loading, setLoading]     = useState(!!article)
  const [toast, setToast]         = useState<Toast>(null)
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit')

  // ── Version history ─────────────────────────────────────────────────────────
  const [revisions, setRevisions]           = useState<Revision[]>([])
  const [selectedRevId, setSelectedRevId]   = useState('')
  const [loadingRevision, setLoadingRevision] = useState(false)

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // ── Load article content ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!article?.id) return

    ;(async () => {
      setLoading(true)
      try {
        const [{ article: full }, { revisions: revList }] = await Promise.all([
          articlesApi.get(article.id),
          articlesApi.listRevisions(article.id),
        ])
        setMarkdown(full.markdown_content ?? '')
        setRevisions(revList)
      } catch (err) {
        showToast('error', `Failed to load article: ${err instanceof Error ? err.message : 'Unknown error'}`)
      } finally {
        setLoading(false)
      }
    })()
  }, [article?.id])

  // ── Auto-generate slug from title ────────────────────────────────────────────
  const handleTitleChange = useCallback((val: string) => {
    setTitle(val)
    if (!article?.id && !slug) {
      // Auto-generate slug only for new articles
      const generated = val
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .slice(0, 80)
      setSlug(generated)
    }
  }, [article?.id, slug])

  function showToast(type: 'success' | 'error', message: string) {
    setToast({ type, message })
    setTimeout(() => setToast(null), 4000)
  }

  // ── Save handler ─────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!slug.trim() || !title.trim() || !markdown.trim()) {
      showToast('error', 'Slug, title, and content are required')
      return
    }

    setSaving(true)
    try {
      const { id } = await articlesApi.save({
        id: article?.id,
        slug: slug.trim(),
        title: title.trim(),
        category: category.trim() || undefined,
        tags: tags.trim() || undefined,
        markdown_content: markdown,
        meta_title: metaTitle.trim() || undefined,
        meta_description: metaDesc.trim() || undefined,
        is_published: isPublished ? 1 : 0,
      })
      showToast('success', '✓ Article saved successfully')
      onSaved(id)
    } catch (err) {
      showToast('error', `Save failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setSaving(false)
    }
  }

  // ── Version restore ──────────────────────────────────────────────────────────
  async function handleRestoreRevision(revId: string) {
    if (!revId || !article?.id) return
    if (!confirm('Replace current editor content with this revision?')) return

    setLoadingRevision(true)
    try {
      const { revision } = await articlesApi.getRevision(article.id, revId)
      setMarkdown(revision.markdown_content ?? '')
      setSelectedRevId('')
      showToast('success', '↩ Revision restored — save to apply')
    } catch (err) {
      showToast('error', `Failed to load revision: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setLoadingRevision(false)
    }
  }

  // ── Tab shortspace in textarea ────────────────────────────────────────────────
  function handleTextareaKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Tab') {
      e.preventDefault()
      const ta = textareaRef.current
      if (!ta) return
      const start = ta.selectionStart
      const end = ta.selectionEnd
      const newVal = markdown.substring(0, start) + '  ' + markdown.substring(end)
      setMarkdown(newVal)
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + 2 }, 0)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, gap: 12, color: 'var(--text-muted)' }}>
        <span style={{ width: 20, height: 20, border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
        Loading article…
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 0 }} className="animate-fade-in">
      {/* ── Top Bar ─────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 24px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-glass)',
        backdropFilter: 'blur(8px)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} id="btn-editor-back">
            ← Back
          </button>
          <h2 style={{ fontSize: '1rem', fontWeight: 600 }}>
            {article?.id ? 'Edit Article' : 'New Article'}
          </h2>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Published toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <div
              onClick={() => setIsPublished(!isPublished)}
              style={{
                width: 36,
                height: 20,
                borderRadius: 10,
                background: isPublished ? 'var(--accent)' : 'var(--bg-glass-light)',
                border: `1px solid ${isPublished ? 'var(--accent)' : 'var(--border)'}`,
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                flexShrink: 0,
              }}
            >
              <span style={{
                position: 'absolute',
                top: 2,
                left: isPublished ? 18 : 2,
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: '#fff',
                transition: 'left 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
            </div>
            <span style={{ fontSize: '0.8rem', color: isPublished ? 'var(--success)' : 'var(--text-muted)' }}>
              {isPublished ? 'Published' : 'Draft'}
            </span>
          </label>

          {/* Revision picker */}
          {article?.id && revisions.length > 0 && (
            <select
              id="select-version-history"
              className="form-select"
              style={{ width: 200, padding: '5px 32px 5px 10px', fontSize: '0.8rem' }}
              value={selectedRevId}
              onChange={(e) => {
                setSelectedRevId(e.target.value)
                handleRestoreRevision(e.target.value)
              }}
              disabled={loadingRevision}
            >
              <option value="">↩ Version History ({revisions.length})</option>
              {revisions.map((rev) => (
                <option key={rev.id} value={rev.id}>
                  {new Date(rev.saved_at).toLocaleString()}
                </option>
              ))}
            </select>
          )}

          <button
            id="btn-save-article"
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <><span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} /> Saving…</>
            ) : (
              <>💾 Save Article</>
            )}
          </button>
        </div>
      </div>

      {/* ── Main body ───────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Metadata panel */}
        <div style={{
          width: 280,
          flexShrink: 0,
          borderRight: '1px solid var(--border)',
          padding: '20px 18px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          background: 'rgba(9,20,19,0.4)',
        }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Metadata
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="input-title">Title *</label>
            <input
              id="input-title"
              className="form-input"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Article title"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="input-slug">Slug *</label>
            <input
              id="input-slug"
              className="form-input"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="url-friendly-slug"
              style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="input-category">Category</label>
            <input
              id="input-category"
              className="form-input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. tutorial, news"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="input-tags">Tags</label>
            <input
              id="input-tags"
              className="form-input"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="tag1, tag2, tag3"
            />
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Comma-separated</div>
          </div>

          <hr className="divider" />

          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            SEO
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="input-meta-title">Meta Title</label>
            <input
              id="input-meta-title"
              className="form-input"
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              placeholder="Overrides title tag"
            />
            <div style={{ fontSize: '0.7rem', color: metaTitle.length > 60 ? 'var(--warning)' : 'var(--text-muted)' }}>
              {metaTitle.length}/60 chars
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="input-meta-desc">Meta Description</label>
            <textarea
              id="input-meta-desc"
              className="form-textarea"
              value={metaDesc}
              onChange={(e) => setMetaDesc(e.target.value)}
              placeholder="160 char max description for search engines"
              rows={3}
              style={{ minHeight: 80 }}
            />
            <div style={{ fontSize: '0.7rem', color: metaDesc.length > 160 ? 'var(--warning)' : 'var(--text-muted)' }}>
              {metaDesc.length}/160 chars
            </div>
          </div>
        </div>

        {/* Editor + Preview pane */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Tab bar */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-glass-light)',
            flexShrink: 0,
          }}>
            {(['edit', 'preview'] as const).map((tab) => (
              <button
                key={tab}
                id={`tab-${tab}`}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '10px 20px',
                  background: 'none',
                  border: 'none',
                  borderBottom: `2px solid ${activeTab === tab ? 'var(--accent)' : 'transparent'}`,
                  color: activeTab === tab ? 'var(--accent-hover)' : 'var(--text-muted)',
                  fontFamily: 'var(--font-sans)',
                  fontWeight: activeTab === tab ? 600 : 400,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  textTransform: 'capitalize',
                }}
              >
                {tab === 'edit' ? '✏️ Editor' : '👁 Preview'}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'center', paddingRight: 16, gap: 8 }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                {markdown.split(/\s+/).filter(Boolean).length} words
              </span>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                · {markdown.length} chars
              </span>
            </div>
          </div>

          {/* Editor pane */}
          {activeTab === 'edit' && (
            <textarea
              ref={textareaRef}
              id="editor-markdown"
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              onKeyDown={handleTextareaKeyDown}
              placeholder="Write your article in Markdown…&#10;&#10;# Heading&#10;&#10;**Bold**, *italic*, `code`&#10;&#10;- Lists&#10;- And more"
              style={{
                flex: 1,
                width: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                resize: 'none',
                padding: '20px 24px',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.875rem',
                lineHeight: 1.75,
                color: 'var(--text-primary)',
                caretColor: 'var(--accent)',
              }}
            />
          )}

          {/* Preview pane */}
          {activeTab === 'preview' && (
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px 32px',
            }}>
              {markdown.trim() ? (
                <div className="markdown-preview">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {markdown}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">📄</div>
                  <p>Start writing in the editor to see the preview</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`toast toast-${toast.type}`} role="alert" aria-live="polite">
          {toast.message}
        </div>
      )}
    </div>
  )
}
